import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';
import CaseCard from './CaseCard';
import { useCases } from '../hooks/use-cases';
import { timeAgo } from '../../../utils/timeAgo';
import { useFilters } from '../../../context/FilterContext';
import { useAuth } from '../../../contexts/AuthContext';
import { isCoFounder } from '../../../utils/cofounderBadge';

const PAGE_SIZE = 20;

const CaseFeed = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const { species, caseType } = useFilters();
  const { user } = useAuth();

  // Accumulated list across pages
  const [page, setPage] = useState(1);
  const [allCases, setAllCases] = useState([]);

  // Reset to page 1 whenever filters or search change
  useEffect(() => {
    setPage(1);
    setAllCases([]);
  }, [species, caseType, q]);

  const { data, isLoading, isFetching, isError } = useCases(page, PAGE_SIZE, { species, caseType, q });

  // Append new page results to accumulated list
  useEffect(() => {
    const incoming = data?.data;
    if (!incoming) return;
    setAllCases((prev) =>
      page === 1 ? incoming : [...prev, ...incoming.filter((c) => !prev.some((p) => p.id === c.id))]
    );
  }, [data, page]);

  const clearSearch = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const total = data?.meta?.total ?? 0;
  const hasMore = allCases.length < total;

  const loadMore = () => setPage((p) => p + 1);

  // Initial load spinner
  if (isLoading && page === 1) {
    return (
      <div className="flex-1 flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 py-10 text-center text-red-500">
        Error loading cases. Please try again.
      </div>
    );
  }

  const cases = allCases;

  return (
    <div className="flex-1">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-black">Clinical Cases</h1>
        <p className="text-sm text-light-gray mt-1">Collaborate with veterinary professionals worldwide</p>
      </div>

      {q && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <Search size={16} className="text-indigo-600" />
            <span>Showing cases matching "<span className="font-semibold">{q}</span>"</span>
          </div>
          <button
            onClick={clearSearch}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            <X size={14} /> Clear search
          </button>
        </div>
      )}
      <div className="space-y-4">
        {cases.map((backendCase) => {
          const isAuthorCurrentUser = Boolean(user?.id && (user.id === backendCase.authorId || user.id === backendCase.author?.id));
          const authorObj = backendCase.author || {};
          const rawName = authorObj.name || (isAuthorCurrentUser ? user?.name : 'Unknown');
          const authorName = authorObj.role === 'student' || isCoFounder(authorObj) || (isAuthorCurrentUser && isCoFounder(user)) ? rawName : (rawName.startsWith('Dr.') ? rawName : `Dr. ${rawName}`);
          const authorRole = authorObj.jobRole || (isAuthorCurrentUser ? user?.jobRole : null) || (authorObj.role === 'student' ? 'Student' : (authorObj.specialization || 'Veterinarian'));
          const authorAvatar = authorObj.avatarUrl || (isAuthorCurrentUser ? user?.avatarUrl : null);

          // Map backend data to UI format
          const mappedCase = {
            id: backendCase.id,
            author: authorName,
            specialty: authorRole,
            jobRole: authorRole,
            avatarUrl: authorAvatar,
            timeAgo: timeAgo(backendCase.createdAt),
            tags: [], // Keep empty per user instructions to hide top badges
            title: backendCase.title,
            description: backendCase.content,
            hashtags: backendCase.tags || [],
            supportCount: backendCase.likes || 0,
            commentCount: backendCase.clinicalInputs?.length || backendCase._count?.clinicalInputs || 0,
            isLiked: backendCase.likedBy?.includes(user?.id),
            _restricted: backendCase._restricted,
            _accessStatus: backendCase._accessStatus,
            isRestricted: backendCase.isRestricted,
            postType: backendCase.postType,
            discussionType: backendCase.discussionType,
            media: backendCase.media || [],
          };

          return <CaseCard key={mappedCase.id} caseData={mappedCase} />;
        })}
        {cases.length === 0 && !isLoading && (
          <div className="text-center py-10 text-gray-500">
            No cases found. Be the first to share one!
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center pt-2 pb-4">
            <button
              onClick={loadMore}
              disabled={isFetching}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-sm font-semibold text-gray-700 rounded-xl hover:border-indigo-300 hover:text-indigo-700 shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isFetching ? (
                <><Loader2 size={15} className="animate-spin" /> Loading…</>
              ) : (
                <><ChevronDown size={15} /> Load More</>  
              )}
            </button>
          </div>
        )}

        {/* All-loaded indicator */}
        {!hasMore && cases.length > 0 && (
          <p className="text-center text-xs text-gray-400 py-4">You&apos;ve seen all {total} posts</p>
        )}
      </div>
    </div>
  );
};

export default CaseFeed;